import type { Dispatch, RefObject, SetStateAction } from 'react';
import { createContext } from 'react';
import type { RemirrorJSON } from '@remirror/core';
import type { BrandInsightsCapabilities } from '@trinity/chat';
import type { RemirrorChatInputRef } from '@trinity/chat-components/components/Chat/RemirrorInput/RemirrorChatInput';
import type { MicState } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { createNamedContext } from '@trinity/chat-components/utils/react';
import type { ILink } from './interfaces';

type SideBarExpandedContextValue = [boolean, Dispatch<SetStateAction<boolean>>];
type SidePanelExpandedContextValue = [boolean, Dispatch<SetStateAction<boolean>>];

export type AuraChatContextType = {
  canToggleAuraChat: boolean;
  auraChatStatus: boolean;
  setAuraChatStatus: Dispatch<SetStateAction<boolean>>;
};

export const SideBarExpandedContext = createContext<SideBarExpandedContextValue>(null);
export const SidePanelExpandedContext = createContext<SidePanelExpandedContextValue>(null);
export const BrandInsightsModalContext = createContext<Modal.ToggleContext>([false, () => {}]);
export const InputControlsContext = createNamedContext<InputControlsContextValue>(
  undefined,
  'InputControls.Context'
);
export const LinkContext = createContext<ILink.LinkContextValue>(undefined);
export const LinkDispatchContext = createContext<Dispatch<ILink.Action>>(undefined);
export const ChatViewTypeContext = createContext<ChatViewType>(undefined);
export const ChatHomeButtonVisibleContext = createContext<boolean>(true);
export const ChatContextSelectorVisibleContext = createContext<boolean>(true);
export const CapabilitiesContext = createContext<BrandInsightsCapabilities>({
  canApplyFilters: false,
  canViewTraces: false,
  canViewExternalLinks: false,
});
export const AuraChatContext = createContext<AuraChatContextType>({
  canToggleAuraChat: false,
  auraChatStatus: false,
  setAuraChatStatus: () => {},
});

export declare namespace Modal {
  export type ToggleContext = [isOpen: boolean, toggleModal: (open?: boolean) => void];
}

export enum ChatViewType {
  Modal = 'modal',
  Page = 'page',
}

type SpeechRecognitionContextValue = {
  errorMsg: string;
  interimResult: string;
  isSupported: () => boolean;
  listening: boolean;
  micState: MicState;
  start: (prefix?: string) => void;
  stop: (debug: unknown) => Promise<void>;
};

type InputControlsContextValue = {
  insertTextAtCursor: (text: string) => void;
  replaceInput: (text: string) => void;
  replaceInputDoc: (doc: RemirrorJSON) => void;
  onClickMicrophone: () => void;
  onClickSubmit: () => void;
  onClickVoiceMode: () => void;
  remirrorRef: RefObject<RemirrorChatInputRef>;
  soundLevelRef: React.RefObject<HTMLDivElement>;
  speechRecognition: SpeechRecognitionContextValue;
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
};
